// src/components/Transactions.jsx
import React, { useEffect, useState, useMemo } from "react";
import { adminGet } from "../../../Services";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  GridStateModule,
  RowSelectionModule,
  ModuleRegistry,
} from "ag-grid-community";
import {
  Container,
  Spinner,
  Form,
  Modal,
  Row,
  Col,
  Card,
  Button,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { formatDateTime } from "../../../utils/helpers";

ModuleRegistry.registerModules([
  RowSelectionModule,
  GridStateModule,
  ClientSideRowModelModule,
]);

const Transactions = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridApi, setGridApi] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    sortField: "createdAt",
    sortOrder: "DESC",
  });
  const [search, setSearch] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeImage, setActiveImage] = useState("");

  const handleImageClick = (imgUrl) => {
    setActiveImage(imgUrl);
    setShowImageModal(true);
  };

  const fetchTransactions = () => {
    const query = new URLSearchParams({
      page: pagination.page,
      limit: pagination.limit,
      ...(search ? { search: search } : {}),
      ...(pagination.sortField ? { sortBy: pagination.sortField } : {}),
      ...(pagination.sortOrder ? { sortOrder: pagination.sortOrder } : {}),
    }).toString();

    setLoading(true);
    adminGet(`/api/admin/transactions?${query}`)
      .then((res) => {
        setRowData(res.data.transactions || []);
        setPagination((p) => ({
          ...p,
          total: res.data.total || 0,
          page: res.data.currentPage,
        }));
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTransactions();
  }, [
    pagination.page,
    pagination.limit,
    pagination.sortField,
    pagination.sortOrder,
    search,
  ]);

  const columnDefs = [
    {
      headerName: "Transaction ID",
      field: "transaction_id",
      sortable: true,
    },
    {
      headerName: "Member Name",
      valueGetter: (params) => {
        if (params.data.Member) {
          return params.data.Member.name;
        }
        if (params.data.PendingMember) {
          return params.data.PendingMember.name;
        }
        return params.data.member_phone;
      },
      sortable: true,
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      valueFormatter: (params) => `₹ ${params.value}`,
    },
    {
      headerName: "Status",
      field: "transaction_type",
      sortable: true,
      cellRenderer: (params) => (
        <Badge
          bg={
            params.value === "success"
              ? "success"
              : params.value === "failed"
              ? "danger"
              : params.value === "cancelled"
              ? "secondary"
              : "warning"
          }
        >
          {params.value}
        </Badge>
      ),
    },
    {
      headerName: "Date",
      field: "createdAt",
      sortable: true,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: (params) => {
        // 1. Define the disabled condition
        const isDisabled =
          !params.data.member_id && !params.data.pending_member_id;

        // 2. Set styles based on the condition
        const className = isDisabled ? "text-muted" : "text-primary";
        const style = {
          cursor: isDisabled ? "not-allowed" : "pointer",
          textAlign: "center",
          opacity: isDisabled ? 0.5 : 1, // Make it visually faded
        };

        // 3. Create a click handler that respects the disabled state
        const handleClick = () => {
          // Do nothing if disabled
          if (isDisabled) {
            return;
          }

          // Original logic
          if (params.data.member_id) {
            setSelectedData(params.data.Member);
          } else {
            setSelectedData(params.data.PendingMember);
          }
          console.log(params.data);
          setShowModal(true);
        };

        return (
          <div className={className} style={style} onClick={handleClick}>
            <i className="bi bi-eye" style={{ fontSize: "1.5rem" }}></i>
          </div>
        );
      },
      pinned: "right",
      width: 80,
    },
  ];

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  // const onSortChanged = () => {
  //   if (!gridApi) return;
  //   const sortModel = gridApi.getSortModel();
  //   if (sortModel.length) {
  //     setPagination((p) => ({
  //       ...p,
  //       sortField: sortModel[0].colId,
  //       sortOrder: sortModel[0].sort?.toUpperCase() === "ASC" ? "ASC" : "DESC",
  //       page: 1,
  //     }));
  //   } else {
  //     setPagination((p) => ({
  //       ...p,
  //       sortField: "createdAt",
  //       sortOrder: "DESC",
  //       page: 1,
  //     }));
  //   }
  // };

  return (
    <div className="p-2 pt-0">
      <div className="mb-2 d-flex align-items-center justify-content-between ">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          Transactions List
        </h3>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Form.Control
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "250px", marginRight: 10 }}
            />
          </div>
          <Form.Select
            style={{ width: "100px" }}
            value={pagination.limit}
            onChange={(e) =>
              setPagination((p) => ({
                ...p,
                limit: parseInt(e.target.value),
                page: 1,
              }))
            }
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </div>
      </div>

      <div className="position-relative">
        {loading && (
          <div className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        <div
          className="ag-theme-alpine"
          style={{ height: "80vh", width: "100%" }}
        >
          <AgGridReact
            columnDefs={columnDefs}
            rowModelType="clientSide"
            rowData={rowData}
            cacheBlockSize={pagination.limit}
            pagination={true}
            paginationPageSize={pagination.limit}
            onGridReady={onGridReady}
            // onSortChanged={onSortChanged}
            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading...</span>'
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No records found.</span>'
          />
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="outline-secondary"
              disabled={pagination.page === 1}
              onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
            >
              Previous
            </Button>
            <span>
              Page {pagination.page} of{" "}
              {Math.ceil((pagination.total || 0) / pagination.limit) || 1}
            </span>
            <Button
              variant="outline-secondary"
              disabled={pagination.page * pagination.limit >= pagination.total}
              onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
     {/*Modal*/}
           <Modal
             show={showModal}
             onHide={() => setShowModal(false)}
             size="lg"
             centered
             className="application-review-modal"
           >
             <Modal.Header closeButton className="modal-header-custom">
               <Modal.Title>
                 <i className="bi bi-clipboard-check me-2"></i>
                 Review Application
               </Modal.Title>
             </Modal.Header>
             <Modal.Body className="p-0">
               <Row className="g-0">
                 <Col md={8} className="p-4">
                   <div className="section-title fs-5">
                     <i className="bi bi-person-badge me-2"></i>
                     Personal Information
                   </div>
     
                   <Row>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Full Name</label>
                         <p>{selectedData.name || "Not provided"}</p>
                       </div>
                     </Col>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Date of Birth</label>
                         <p>{selectedData.dob || "Not provided"}</p>
                       </div>
                     </Col>
                   </Row>
     
                   <Row>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Profession</label>
                         <p>{selectedData.profession || "Not provided"}</p>
                       </div>
                     </Col>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Mobile Number</label>
                         <p>{selectedData.mobile_no || "Not provided"}</p>
                       </div>
                     </Col>
                   </Row>
     
                   <div className="data-field">
                     <label>Email Address</label>
                     <p>{selectedData.email || "Not provided"}</p>
                   </div>
     
                   <div className="section-title mt-4 fs-5">
                     <i className="bi bi-geo-alt me-2"></i>
                     Address Details
                   </div>
     
                   <Row>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>State</label>
                         <p>{selectedData.state || "Not provided"}</p>
                       </div>
                     </Col>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>District</label>
                         <p>{selectedData.district || "Not provided"}</p>
                       </div>
                     </Col>
                   </Row>
     
                   <Row>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Block</label>
                         <p>{selectedData.block || "Not provided"}</p>
                       </div>
                     </Col>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>PIN Code</label>
                         <p>{selectedData.pin || "Not provided"}</p>
                       </div>
                     </Col>
                   </Row>
     
                   <div className="section-title mt-4 fs-5">
                     <i className="bi bi-card-checklist me-2"></i>
                     Identification & Documents
                   </div>
     
                   <Row>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Aadhaar Number</label>
                         <p>{selectedData.aadhaar_no || "Not provided"}</p>
                       </div>
                     </Col>
                     <Col sm={6}>
                       <div className="data-field">
                         <label>Vehicle No</label>
                         <p>{selectedData.vehicle_no || "Not provided"}</p>
                       </div>
                     </Col>
                   </Row>
                 </Col>
     
                 <Col md={4} className="documents-sidebar">
                   <div className="p-3">
                     <h6 className="text-center mb-3">
                       <i className="bi bi-images me-2"></i>
                       Uploaded Documents
                     </h6>
     
                     {selectedData.photo_url && (
                       <Card className="mb-3 document-card">
                         <Card.Header className="text-center">
                           Profile Photo
                         </Card.Header>
                         <Card.Body className="text-center p-2">
                           <img
                             src={`${selectedData.photo_url}`}
                             alt="Profile"
                             className="uploaded-image"
                             style={{ cursor: "pointer" }}
                             onClick={() =>
                               handleImageClick(`${selectedData.photo_url}`)
                             }
                           />
                         </Card.Body>
                       </Card>
                     )}
     
                     {selectedData.aadhaar_url && (
                       <Card className="document-card">
                         <Card.Header className="text-center">
                           Aadhaar Card
                         </Card.Header>
                         <Card.Body className="text-center p-2">
                           <img
                             src={`${selectedData.aadhaar_url}`}
                             alt="Aadhaar Card"
                             className="uploaded-image"
                             style={{ cursor: "pointer" }}
                             onClick={() =>
                               handleImageClick(`${selectedData.aadhaar_url}`)
                             }
                           />
                         </Card.Body>
                       </Card>
                     )}
     
                     {!selectedData.photo_url && !selectedData.aadhaar_url && (
                       <div className="text-center text-muted py-4">
                         <i className="bi bi-inbox display-4"></i>
                         <p>No documents uploaded</p>
                       </div>
                     )}
                   </div>
                 </Col>
               </Row>
             </Modal.Body>
             <Modal.Footer className="modal-footer-custom">
               <Button
                 variant="outline-secondary"
                 onClick={() => setShowModal(false)}
               >
                 Close
               </Button>
               
             </Modal.Footer>
           </Modal>
     
           {/* Image Preview Modal */}
           <Modal
             show={showImageModal}
             onHide={() => setShowImageModal(false)}
             size="lg"
             centered
           >
             <Modal.Body className="text-center">
               <img
                 src={activeImage}
                 alt="Preview"
                 style={{ maxWidth: "100%", maxHeight: "80vh" }}
               />
             </Modal.Body>
           </Modal>
    </div>
  );
};

export default Transactions;
